/* eslint-disable max-lines */
/**
 * Country name normalization between TMDB and map library formats
 *
 * TMDB often uses full formal names (e.g., "United States of America")
 * while the map library uses shorter names (e.g., "United States")
 */

// Map TMDB country names to map library country names
const COUNTRY_NAME_MAP: Record<string, string> = {
  // ISO 2-letter codes to full names (for database values)
  US: "United States of America",
  GB: "United Kingdom",
  CA: "Canada",
  AU: "Australia",
  NZ: "New Zealand",
  IE: "Ireland",

  // Europe
  FR: "France",
  DE: "Germany",
  IT: "Italy",
  ES: "Spain",
  PT: "Portugal",
  NL: "Netherlands",
  BE: "Belgium",
  CH: "Switzerland",
  AT: "Austria",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  IS: "Iceland",
  PL: "Poland",
  CZ: "Czechia",
  SK: "Slovakia",
  HU: "Hungary",
  RO: "Romania",
  BG: "Bulgaria",
  GR: "Greece",
  HR: "Croatia",
  SI: "Slovenia",
  RS: "Serbia",
  LT: "Lithuania",
  LV: "Latvia",
  EE: "Estonia",
  LU: "Luxembourg",
  MT: "Malta",
  CY: "Cyprus",

  // Eastern Europe & Russia
  RU: "Russia",
  UA: "Ukraine",
  BY: "Belarus",
  MD: "Moldova",

  // Asia
  CN: "China",
  JP: "Japan",
  KR: "South Korea",
  IN: "India",
  TH: "Thailand",
  VN: "Vietnam",
  PH: "Philippines",
  ID: "Indonesia",
  MY: "Malaysia",
  SG: "Singapore",
  TW: "Taiwan",
  HK: "Hong Kong",
  PK: "Pakistan",
  BD: "Bangladesh",
  LK: "Sri Lanka",
  MM: "Myanmar",
  KH: "Cambodia",
  LA: "Laos",
  NP: "Nepal",
  MN: "Mongolia",

  // Middle East
  TR: "Turkey",
  IR: "Iran",
  IQ: "Iraq",
  SA: "Saudi Arabia",
  AE: "United Arab Emirates",
  IL: "Israel",
  JO: "Jordan",
  LB: "Lebanon",
  SY: "Syria",
  YE: "Yemen",
  OM: "Oman",
  KW: "Kuwait",
  QA: "Qatar",
  BH: "Bahrain",

  // Africa
  EG: "Egypt",
  ZA: "South Africa",
  NG: "Nigeria",
  KE: "Kenya",
  ET: "Ethiopia",
  GH: "Ghana",
  TZ: "Tanzania",
  UG: "Uganda",
  MA: "Morocco",
  DZ: "Algeria",
  TN: "Tunisia",
  SN: "Senegal",
  CI: "Ivory Coast",
  CM: "Cameroon",
  ZW: "Zimbabwe",

  // Americas
  MX: "Mexico",
  BR: "Brazil",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  VE: "Venezuela",
  EC: "Ecuador",
  UY: "Uruguay",
  PY: "Paraguay",
  BO: "Bolivia",
  CR: "Costa Rica",
  PA: "Panama",
  CU: "Cuba",
  DO: "Dominican Republic",
  GT: "Guatemala",
  HN: "Honduras",
  SV: "El Salvador",
  NI: "Nicaragua",
  JM: "Jamaica",
  TT: "Trinidad and Tobago",

  // Full name variations to canonical names
  "United States of America": "United States of America",
  USA: "United States of America",
  "United States": "United States of America",
  "United Kingdom": "United Kingdom",
  UK: "United Kingdom",
  "Great Britain": "United Kingdom",
  "South Korea": "South Korea",
  Korea: "South Korea",
  "Republic of Korea": "South Korea",
  Russia: "Russia",
  "Russian Federation": "Russia",
  "Czech Republic": "Czechia",
  "The Netherlands": "Netherlands",
};

/**
 * Normalize a country name from TMDB format to map format
 */
export function normalizeCountryName(tmdbCountryName: string): string {
  // Check if we have a mapping
  if (COUNTRY_NAME_MAP[tmdbCountryName]) {
    return COUNTRY_NAME_MAP[tmdbCountryName];
  }

  // Return as-is if no mapping found
  return tmdbCountryName;
}

/**
 * Get all unique countries from a list of titles
 */
export function extractCountries(titles: Array<{ countries?: string[] }>): string[] {
  const countrySet = new Set<string>();

  for (const title of titles) {
    if (title.countries) {
      for (const country of title.countries) {
        const normalized = normalizeCountryName(country);
        countrySet.add(normalized);
      }
    }
  }

  return Array.from(countrySet).sort((a, b) => a.localeCompare(b));
}

/**
 * Count titles per country
 */
export function countTitlesByCountry(titles: Array<{ country?: string | null }>): Map<string, number> {
  const countMap = new Map<string, number>();

  for (const title of titles) {
    if (title.country) {
      const normalized = normalizeCountryName(title.country);
      countMap.set(normalized, (countMap.get(normalized) || 0) + 1);
    }
  }

  return countMap;
} /**
 * Calculate opacity for a country based on title count
 * More titles = higher opacity (brighter glow)
 */
export function calculateCountryOpacity(titleCount: number, maxCount: number): number {
  if (titleCount === 0) return 0;

  const minOpacity = 0.2; // Faint glow for few titles
  const maxOpacity = 0.9; // Bright glow for many titles

  const ratio = titleCount / maxCount;
  return minOpacity + ratio * (maxOpacity - minOpacity);
}

/**
 * Map normalized country names to ISO 3166-1 alpha-3 codes
 * Used for Mapbox country-boundaries-v1 tileset feature state
 */
const COUNTRY_NAME_TO_ISO3: Record<string, string> = {
  // Americas
  "United States of America": "USA",
  Canada: "CAN",
  Mexico: "MEX",
  Brazil: "BRA",
  Argentina: "ARG",
  Chile: "CHL",
  Colombia: "COL",
  Peru: "PER",
  Venezuela: "VEN",
  Ecuador: "ECU",
  Uruguay: "URY",
  Paraguay: "PRY",
  Bolivia: "BOL",
  "Costa Rica": "CRI",
  Panama: "PAN",
  Cuba: "CUB",
  "Dominican Republic": "DOM",
  Guatemala: "GTM",
  Honduras: "HND",
  "El Salvador": "SLV",
  Nicaragua: "NIC",
  Jamaica: "JAM",
  "Trinidad and Tobago": "TTO",

  // Europe - Western
  "United Kingdom": "GBR",
  France: "FRA",
  Germany: "DEU",
  Italy: "ITA",
  Spain: "ESP",
  Portugal: "PRT",
  Netherlands: "NLD",
  Belgium: "BEL",
  Switzerland: "CHE",
  Austria: "AUT",
  Ireland: "IRL",
  Luxembourg: "LUX",

  // Europe - Nordic
  Sweden: "SWE",
  Norway: "NOR",
  Denmark: "DNK",
  Finland: "FIN",
  Iceland: "ISL",

  // Europe - Eastern
  Poland: "POL",
  Czechia: "CZE",
  Slovakia: "SVK",
  Hungary: "HUN",
  Romania: "ROU",
  Bulgaria: "BGR",
  Greece: "GRC",
  Croatia: "HRV",
  Slovenia: "SVN",
  Serbia: "SRB",
  Lithuania: "LTU",
  Latvia: "LVA",
  Estonia: "EST",
  Malta: "MLT",
  Cyprus: "CYP",

  // Europe - Eastern & Russia
  Russia: "RUS",
  Ukraine: "UKR",
  Belarus: "BLR",
  Moldova: "MDA",

  // Asia - East
  China: "CHN",
  Japan: "JPN",
  "South Korea": "KOR",
  Taiwan: "TWN",
  "Hong Kong": "HKG",
  Mongolia: "MNG",

  // Asia - Southeast
  Thailand: "THA",
  Vietnam: "VNM",
  Philippines: "PHL",
  Indonesia: "IDN",
  Malaysia: "MYS",
  Singapore: "SGP",
  Myanmar: "MMR",
  Cambodia: "KHM",
  Laos: "LAO",

  // Asia - South
  India: "IND",
  Pakistan: "PAK",
  Bangladesh: "BGD",
  "Sri Lanka": "LKA",
  Nepal: "NPL",

  // Middle East
  Turkey: "TUR",
  Iran: "IRN",
  Iraq: "IRQ",
  "Saudi Arabia": "SAU",
  "United Arab Emirates": "ARE",
  Israel: "ISR",
  Jordan: "JOR",
  Lebanon: "LBN",
  Syria: "SYR",
  Yemen: "YEM",
  Oman: "OMN",
  Kuwait: "KWT",
  Qatar: "QAT",
  Bahrain: "BHR",

  // Africa
  Egypt: "EGY",
  "South Africa": "ZAF",
  Nigeria: "NGA",
  Kenya: "KEN",
  Ethiopia: "ETH",
  Ghana: "GHA",
  Tanzania: "TZA",
  Uganda: "UGA",
  Morocco: "MAR",
  Algeria: "DZA",
  Tunisia: "TUN",
  Senegal: "SEN",
  "Ivory Coast": "CIV",
  Cameroon: "CMR",
  Zimbabwe: "ZWE",

  // Oceania
  Australia: "AUS",
  "New Zealand": "NZL",
};

/**
 * Convert normalized country name to ISO 3166-1 alpha-3 code for Mapbox
 * Returns null if no mapping exists
 */
export function countryNameToISO3(countryName: string): string | null {
  return COUNTRY_NAME_TO_ISO3[countryName] || null;
}

/**
 * Get ISO3 code from any country name format (TMDB, 2-letter, full name, etc.)
 */
export function getISO3Code(countryInput: string): string | null {
  const normalized = normalizeCountryName(countryInput);
  return countryNameToISO3(normalized);
}

/**
 * ISO 3166-1 alpha-2 to alpha-3 conversion
 * Mapbox's country-boundaries-v1 uses alpha-2 codes (e.g., "US", "FR")
 * but we use alpha-3 codes (e.g., "USA", "FRA") for consistency
 */
const ISO2_TO_ISO3: Record<string, string> = {
  // Americas
  US: "USA",
  CA: "CAN",
  MX: "MEX",
  BR: "BRA",
  AR: "ARG",
  CL: "CHL",
  CO: "COL",
  PE: "PER",
  VE: "VEN",
  EC: "ECU",
  UY: "URY",
  PY: "PRY",
  BO: "BOL",
  CR: "CRI",
  PA: "PAN",
  CU: "CUB",
  DO: "DOM",
  GT: "GTM",
  HN: "HND",
  SV: "SLV",
  NI: "NIC",
  JM: "JAM",
  TT: "TTO",
  // Europe - Western
  GB: "GBR",
  FR: "FRA",
  DE: "DEU",
  IT: "ITA",
  ES: "ESP",
  PT: "PRT",
  NL: "NLD",
  BE: "BEL",
  CH: "CHE",
  AT: "AUT",
  IE: "IRL",
  LU: "LUX",
  // Europe - Nordic
  SE: "SWE",
  NO: "NOR",
  DK: "DNK",
  FI: "FIN",
  IS: "ISL",
  // Europe - Eastern
  PL: "POL",
  CZ: "CZE",
  SK: "SVK",
  HU: "HUN",
  RO: "ROU",
  BG: "BGR",
  GR: "GRC",
  HR: "HRV",
  SI: "SVN",
  RS: "SRB",
  LT: "LTU",
  LV: "LVA",
  EE: "EST",
  MT: "MLT",
  CY: "CYP",
  // Russia & Eastern Europe
  RU: "RUS",
  UA: "UKR",
  BY: "BLR",
  MD: "MDA",
  // Asia - East
  CN: "CHN",
  JP: "JPN",
  KR: "KOR",
  TW: "TWN",
  HK: "HKG",
  MN: "MNG",
  // Asia - Southeast
  TH: "THA",
  VN: "VNM",
  PH: "PHL",
  ID: "IDN",
  MY: "MYS",
  SG: "SGP",
  MM: "MMR",
  KH: "KHM",
  LA: "LAO",
  // Asia - South
  IN: "IND",
  PK: "PAK",
  BD: "BGD",
  LK: "LKA",
  NP: "NPL",
  // Middle East
  TR: "TUR",
  IR: "IRN",
  IQ: "IRQ",
  SA: "SAU",
  AE: "ARE",
  IL: "ISR",
  JO: "JOR",
  LB: "LBN",
  SY: "SYR",
  YE: "YEM",
  OM: "OMN",
  KW: "KWT",
  QA: "QAT",
  BH: "BHR",
  // Africa
  EG: "EGY",
  ZA: "ZAF",
  NG: "NGA",
  KE: "KEN",
  ET: "ETH",
  GH: "GHA",
  TZ: "TZA",
  UG: "UGA",
  MA: "MAR",
  DZ: "DZA",
  TN: "TUN",
  SN: "SEN",
  CI: "CIV",
  CM: "CMR",
  ZW: "ZWE",
  // Oceania
  AU: "AUS",
  NZ: "NZL",
};

/**
 * Convert ISO 3166-1 alpha-2 code to alpha-3 code
 * Mapbox uses alpha-2, but our internal system uses alpha-3
 */
export function iso2ToISO3(iso2: string): string | null {
  return ISO2_TO_ISO3[iso2.toUpperCase()] || null;
}

/**
 * Convert ISO 3166-1 alpha-3 code back to normalized country name
 * Returns null if no mapping exists
 */
export function iso3ToCountryName(iso3: string): string | null {
  // Reverse lookup in COUNTRY_NAME_TO_ISO3
  for (const [countryName, code] of Object.entries(COUNTRY_NAME_TO_ISO3)) {
    if (code === iso3) {
      return countryName;
    }
  }
  return null;
}

/**
 * Convert ISO 3166-1 alpha-2 code to normalized country name
 * Convenience function that combines iso2ToISO3 and iso3ToCountryName
 */
export function iso2ToCountryName(iso2: string): string | null {
  const iso3 = iso2ToISO3(iso2);
  return iso3 ? iso3ToCountryName(iso3) : null;
}

/**
 * Country options for use in select/combobox components
 * Exported as array of {value: ISO2 code, label: full name} sorted by label
 */
export const COUNTRY_OPTIONS = Object.keys(ISO2_TO_ISO3)
  .map((iso2) => ({
    value: iso2,
    label: iso2ToCountryName(iso2) || iso2,
  }))
  .filter((opt) => opt.label !== opt.value) // Remove any unmapped codes
  .sort((a, b) => a.label.localeCompare(b.label));
